import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { useToast } from '../../../context/ToastContext';
import { designTokens } from '../../../theme/designTokens';
import api from '../../../services/api';
import TrainerDialog from './TrainerDialog';

interface Trainer {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    trainerSettings?: {
        questionScope?: 'all' | 'assigned';
        assignedLearners?: string[];
    };
    lastLogin?: string | null;
}

const TrainersPage = () => {
    const [trainers, setTrainers] = useState<Trainer[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedTrainer, setSelectedTrainer] = useState<Trainer | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [trainerToDelete, setTrainerToDelete] = useState<Trainer | null>(null);
    const { showToast } = useToast();

    useEffect(() => {
        fetchTrainers();
    }, []);

    const fetchTrainers = async () => {
        try {
            const res = await api.get('/api/users');
            const trainerUsers = res.data.filter((user: any) => user.role === 'trainer');
            setTrainers(trainerUsers);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching trainers", err);
            setLoading(false);
        }
    };

    const handleAddTrainer = () => {
        setSelectedTrainer(null);
        setDialogOpen(true);
    };

    const handleEditTrainer = (trainer: Trainer) => {
        setSelectedTrainer(trainer);
        setDialogOpen(true);
    };

    const handleDeleteClick = (trainer: Trainer) => {
        setTrainerToDelete(trainer);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!trainerToDelete) return;

        try {
            await api.delete(`/api/users/${trainerToDelete._id}`);
            setTrainers(trainers.filter(t => t._id !== trainerToDelete._id));
            setDeleteDialogOpen(false);
            setTrainerToDelete(null);
        } catch (err) {
            console.error("Error deleting trainer", err);
        }
    };

    const handleResendToken = async (trainerId: string) => {
        try {
            const res = await api.post(`/api/users/${trainerId}/resend-token`);
            showToast(res.data.message || 'Lien de connexion renvoyé', 'success');
        } catch (err: any) {
            console.error("Error resending token", err);
            showToast(err.response?.data?.message || 'Erreur lors du renvoi du lien', 'error');
        }
    };

    const handleDialogClose = (refresh?: boolean) => {
        setDialogOpen(false);
        setSelectedTrainer(null);
        if (refresh) {
            fetchTrainers();
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>Nos Formateurs</Typography>
                    <Typography variant="body1" color="text.secondary">
                        Gérez les formateurs de votre organisme
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddTrainer}
                >
                    Ajouter un formateur
                </Button>
            </Box>

            <TableContainer component={Paper} sx={{ ...designTokens.glassCard }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nom</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Email</TableCell>
                            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Scope des questions</TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Apprenants assignés</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {trainers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Box sx={{ py: 4 }}>
                                        <PeopleIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                        <Typography color="text.secondary">
                                            Aucun formateur pour le moment
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            trainers.map((trainer) => (
                                <TableRow key={trainer._id} hover>
                                    <TableCell>
                                        <Typography variant="body1">
                                            {trainer.firstName} {trainer.lastName}
                                        </Typography>
                                        {/* Show email on mobile */}
                                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' } }}>
                                            {trainer.email}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{trainer.email}</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                                        <Chip
                                            label={
                                                trainer.trainerSettings?.questionScope === 'all'
                                                    ? 'Toutes les questions'
                                                    : 'Questions assignées'
                                            }
                                            color={
                                                trainer.trainerSettings?.questionScope === 'all'
                                                    ? 'primary'
                                                    : 'default'
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Chip
                                            label={trainer.trainerSettings?.assignedLearners?.length || 0}
                                            size="small"
                                            icon={<PeopleIcon />}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        {!trainer.lastLogin && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleResendToken(trainer._id)}
                                                color="info"
                                                title="Renvoyer le lien de connexion"
                                                sx={{ mr: 1 }}
                                            >
                                                <AlternateEmailIcon fontSize="small" />
                                            </IconButton>
                                         )}
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditTrainer(trainer)}
                                            color="primary"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteClick(trainer)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add/Edit Dialog */}
            <TrainerDialog
                open={dialogOpen}
                trainer={selectedTrainer}
                onClose={handleDialogClose}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirmer la suppression</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir supprimer le formateur{' '}
                        <strong>
                            {trainerToDelete?.firstName} {trainerToDelete?.lastName}
                        </strong>{' '}
                        ? Cette action est irréversible.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TrainersPage;
